import { useEffect, useState } from "react";
import axios from "axios";
import { API } from "../App";
import { TrendingUp, FileText, Globe, CheckCircle } from "lucide-react";

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [recentArticles, setRecentArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, articlesRes] = await Promise.all([
        axios.get(`${API}/stats`),
        axios.get(`${API}/articles?limit=5`)
      ]);
      setStats(statsRes.data);
      setRecentArticles(articlesRes.data.slice(0, 5));
    } catch (error) {
      console.error("Erreur chargement dashboard:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96" data-testid="dashboard-loading">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8 fade-in" data-testid="dashboard">
      <div>
        <h1 className="text-4xl font-bold text-gray-900 mb-2">Tableau de bord</h1>
        <p className="text-gray-600">Vue d'ensemble de votre outil de veille SEO</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="stat-card" data-testid="stat-sources">
          <div className="flex items-center justify-between mb-4">
            <Globe className="text-[#4F86EF]" size={32} />
          </div>
          <div className="text-3xl font-bold text-gray-900 mb-1">{stats?.sources?.active || 0}</div>
          <div className="text-sm text-gray-600">Sources actives</div>
          <div className="text-xs text-gray-400 mt-1">sur {stats?.sources?.total || 0} total</div>
        </div>

        <div className="stat-card" data-testid="stat-urls">
          <div className="flex items-center justify-between mb-4">
            <TrendingUp className="text-[#4F86EF]" size={32} />
          </div>
          <div className="text-3xl font-bold text-gray-900 mb-1">{stats?.urls?.unprocessed || 0}</div>
          <div className="text-sm text-gray-600">URLs à traiter</div>
          <div className="text-xs text-gray-400 mt-1">sur {stats?.urls?.total || 0} détectées</div>
        </div>

        <div className="stat-card" data-testid="stat-articles">
          <div className="flex items-center justify-between mb-4">
            <FileText className="text-[#4F86EF]" size={32} />
          </div>
          <div className="text-3xl font-bold text-gray-900 mb-1">{stats?.articles?.total || 0}</div>
          <div className="text-sm text-gray-600">Articles générés</div>
          <div className="text-xs text-gray-400 mt-1">{stats?.articles?.draft || 0} brouillons</div>
        </div>

        <div className="stat-card" data-testid="stat-published">
          <div className="flex items-center justify-between mb-4">
            <CheckCircle className="text-[#4F86EF]" size={32} />
          </div>
          <div className="text-3xl font-bold text-gray-900 mb-1">{stats?.articles?.published || 0}</div>
          <div className="text-sm text-gray-600">Articles publiés</div>
        </div>
      </div>

      {/* Recent Articles */}
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Articles récents</h2>
        {recentArticles.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-100 p-12 text-center" data-testid="no-articles">
            <FileText className="mx-auto text-gray-300 mb-4" size={48} />
            <p className="text-gray-600">Aucun article généré pour le moment</p>
            <p className="text-sm text-gray-400 mt-2">Commencez par ajouter des sources et lancer une veille</p>
          </div>
        ) : (
          <div className="table-container" data-testid="recent-articles">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr>
                    <th className="table-header text-left">Titre</th>
                    <th className="table-header text-left">Statut</th>
                    <th className="table-header text-left">Mots-clés</th>
                    <th className="table-header text-left">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {recentArticles.map((article) => (
                    <tr key={article.id} className="table-row" data-testid={`article-${article.id}`}>
                      <td className="px-6 py-4">
                        <div className="font-medium text-gray-900">{article.title}</div>
                        <div className="text-sm text-gray-500 truncate max-w-md">{article.meta_description}</div>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`badge ${
                          article.status === 'published' ? 'badge-success' :
                          article.status === 'ready' ? 'badge-info' : 'badge-default'
                        }`}>
                          {article.status === 'published' ? 'Publié' :
                           article.status === 'ready' ? 'Prêt' : 'Brouillon'}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex flex-wrap gap-1">
                          {article.keywords.slice(0, 3).map((kw, i) => (
                            <span key={i} className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">
                              {kw}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {new Date(article.created_at).toLocaleDateString('fr-FR')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

    </div>
  );
}
